import { AppUpdate, AppUpdateAvailability, AppUpdateResultCode } from '@capawesome/capacitor-app-update';
import { Capacitor } from '@capacitor/core';

export enum AppUpdateError {
  NETWORK_ERROR = 'NETWORK_ERROR',
  INSUFFICIENT_STORAGE = 'INSUFFICIENT_STORAGE',
  CANCELED = 'CANCELED',
  UNKNOWN = 'UNKNOWN',
}

/**
 * Normalizza gli errori provenienti dal plugin di aggiornamento.
 * @param e errore generico restituito dal plugin.
 * @returns Tipo d'errore mappato.
 */
export function mapError(e: unknown): AppUpdateError {
  if (typeof e === 'number') {
    return e === AppUpdateResultCode.CANCELED
      ? AppUpdateError.CANCELED
      : AppUpdateError.UNKNOWN;
  }
  const msg =
    typeof e === 'string' ? e : (e as { message?: string })?.message || '';
  if (/network/i.test(msg)) return AppUpdateError.NETWORK_ERROR;
  if (/storage|space/i.test(msg)) return AppUpdateError.INSUFFICIENT_STORAGE;
  if (/cancel/i.test(msg)) return AppUpdateError.CANCELED;
  return AppUpdateError.UNKNOWN;
}

async function retry<T>(fn: () => Promise<T>): Promise<T> {
  let lastErr: unknown;
  for (let i = 0; i < 2; i++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
    }
  }
  throw mapError(lastErr);
}

/**
 * Recupera lo stato di disponibilità di un aggiornamento.
 * Su iOS restituisce sempre `updateAvailable: false`.
 * @returns Informazioni sull'eventuale aggiornamento disponibile.
 */
export async function getInfo(): Promise<{
  updateAvailable: boolean;
  availableVersion?: string;
}> {
  if (Capacitor.getPlatform() === 'ios') {
    return { updateAvailable: false };
  }
  const info = await retry(() => AppUpdate.getAppUpdateInfo());
  const updateAvailable =
    info.updateAvailability === AppUpdateAvailability.UPDATE_AVAILABLE ||
    info.updateAvailability === AppUpdateAvailability.UPDATE_IN_PROGRESS;
  const availableVersion = info.availableVersionName ?? info.availableVersionCode;
  return { updateAvailable, availableVersion };
}

/**
 * Avvia l'aggiornamento immediato tramite il plugin.
 * Su iOS non esegue alcuna operazione.
 */
export async function performImmediate(): Promise<void> {
  if (Capacitor.getPlatform() === 'ios') return;
  const res = await retry(() => AppUpdate.performImmediateUpdate());
  if (res.code !== AppUpdateResultCode.OK) {
    throw mapError(res.code);
  }
}
